import React, { useEffect, useRef, useState } from "react";
import { Palette, Check } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const OPTIONS = [
  { id: "aurora", name: "Aurora", desc: "Sombre · cyan", swatch: ["#05060E", "#00E5FF", "#7C3AED"] },
  { id: "ember", name: "Ember", desc: "Sombre · chaleureux", swatch: ["#0A0708", "#FF4500", "#FF0055"] },
  { id: "light", name: "Aurora White", desc: "Clair · doux", swatch: ["#E0E5F1", "#00B4D8", "#7C3AED"] },
  { id: "daylight", name: "Daylight", desc: "Clair · coloré", swatch: ["#F0E7FF", "#FF6F91", "#21C7B7"] },
  { id: "mpower", name: "M Power", desc: "Sombre · rayures M", swatch: ["#050609", "#2E6BFF", "#E4002B"] },
  { id: "mlight", name: "M Light", desc: "Clair · bleu électrique", swatch: ["#EEF3FB", "#0057FF", "#E4002B"] },
];

// Compact theme dropdown for headers (public site + app).
export const ThemePicker = ({ className = "" }) => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (opt) => {
    setTheme(opt.id);
    setOpen(false);
    toast.success(`Thème « ${opt.name} » appliqué`);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="theme-picker"
        aria-label="Choisir un thème"
        className="relative w-10 h-10 grid place-items-center rounded-sm border border-white/15 hover:border-aurora-cyan/50 transition-colors"
      >
        <Palette className="w-[18px] h-[18px] text-aurora-cyan" />
      </button>
      {open && (
        <div data-testid="theme-picker-menu" className="popover-solid absolute right-0 mt-2 w-56 rounded-md p-1.5 z-[80]">
          {OPTIONS.map((opt) => {
            const active = theme === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => pick(opt)}
                data-testid={`theme-pick-${opt.id}`}
                className={`theme-preview-host w-full flex flex-col gap-1.5 px-2.5 py-2 rounded-sm text-left transition-colors ${active ? "bg-aurora-cyan/12" : "hover:bg-white/5"}`}
              >
                <span className="flex items-center gap-2.5">
                  <span className="flex gap-1 shrink-0">
                    {opt.swatch.map((c, i) => (
                      <span key={i} className="w-4 h-4 rounded-[3px] border border-white/10" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold flex items-center gap-1.5">{opt.name}{active && <Check className="w-3 h-3 text-aurora-cyan" />}</span>
                    <span className="block font-mono text-[9px] uppercase tracking-widest text-zinc-500">{opt.desc}</span>
                  </span>
                </span>
                <span
                  className="theme-preview-stripe"
                  style={{ backgroundImage: `linear-gradient(90deg, ${opt.swatch[1]} 0 25%, ${opt.swatch[2]} 25% 50%, ${opt.swatch[1]} 50% 75%, ${opt.swatch[2]} 75% 100%)` }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
